import { runInAction } from 'mobx';
import { ApplicationRoutes } from '../../../../../../application/constants/routes';
import { ILocalization, ILocalizationToken } from '../../../../../../application/language/ILocalization';
import { IContainer } from '../../../../../../common/container/IContainer';
import { ConstructorType, inject, injectValue } from '../../../../../../common/container/inject';
import { IError } from '../../../../../../common/error/IError';
import { IValidationErrorData } from '../../../../../../common/error/IValidationErrorData';
import { injectPrimitive } from '../../../../../../common/store/base/injectPrimitive';
import { saveFileInBrowser } from '../../../../../../common/utils/saveFileInBrowser';
import { IApplicationDataModel } from '../../../../../../service/application/data/IApplicationDataModel';
import { ApplicationAcceptStatus, IApplicationModel } from '../../../../../../service/application/entity/IApplicationModel';
import { ICoreSearchAudit } from '../../../../../../service/common/audit/ICoreSearchAudit';
import { ILearningRootService, LearningRootServiceToken } from '../../../../../../service/LearningRootService';
import { UserProjectRoleAction } from '../../../../../../service/projectRole/entity/actions/UserProjectRoleAction';
import { injectRootService } from '../../../../../../service/RootServiceFactory';
import { IRouterService, RouterServiceToken } from '../../../../../../service/route/IRouterService';
import { ISpecificationCategoryModel } from '../../../../../../service/specification/category/ISpecificationCategoryModel';
import { SpecificationCategoryType } from '../../../../../../service/specification/category/ISpecificationCategoryModelConditionMap';
import { ISpecificationEntityModel } from '../../../../../../service/specification/entity/ISpecificationEntityModel';
import { UserSystemRoleModelPermissionMap } from '../../../../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import { IUnitModel } from '../../../../../../service/unit/interface/IUnitModel';
import { LayoutNotificationType } from '../../../../../layout/common/notification/store/ILayoutNotification';

import { IMainLayoutDomainStore } from '../../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { ProjectFormDomain } from '../../../project/form/store/ProjectFormDomain';
import { ApplicationDetailsMode } from './ApplicationDetailsMode';
import { ApplicationDetailsType } from './ApplicationDetailsType';
import { ApplicationDetailUI, defaultEditApplication } from './ApplicationDetailUI';

export class ApplicationDetailDomain {
  private isLoadedApplicationDependencies: boolean = false;
  private isLoadedApplicationDataDependencies: boolean = false;
  public projectFormDomain: ProjectFormDomain | null = null;
  private i18n: ILocalization = inject<ILocalization>(ILocalizationToken);

  constructor(
    public layoutDomain: IMainLayoutDomainStore,
    public ui: ApplicationDetailUI = new (injectValue<ConstructorType<ApplicationDetailUI>>(ApplicationDetailUI))(),
    protected router: IRouterService = inject<IRouterService>(RouterServiceToken),
    protected rootService = injectRootService(layoutDomain.serviceType.value),
    private container: IContainer | null = null,
    public performerTypeId: string | undefined = ui?.editApplication?.entity?.performerTypeId || undefined,
    public editApplicationCopy: any = ui?.editApplication?.entity || undefined,
    public paginationPage = injectPrimitive<number>(1),
    public isDisabledSendToManager = injectPrimitive<boolean>(false),
    private learningRootService = inject<ILearningRootService>(LearningRootServiceToken),
  ) {}

  private async loadViewSpecifications(application: IApplicationModel) {
    const ids = application.specificationsIds || [];
    if (!ids.length) {
      this.ui.applicationSpecifications.setList([]);
      this.ui.applicationSpecificationsCategories.setList([]);
      return;
    }

    const applicationSpecifications = await this.rootService.specification.entity.search({
      limit: 100000,
      filter: { ids: { in: ids } },
    });

    const categoryIds = (applicationSpecifications?.data || []).reduce((acc: string[], spec: any) => {
      if (spec?.categoryId && !acc.includes(spec.categoryId)) acc.push(spec.categoryId);
      return acc;
    }, []);

    const applicationCategories =
      categoryIds.length > 0
        ? await this.rootService.specification.category.search({
            limit: 100000,
            filter: { ids: { in: categoryIds } },
          })
        : { data: [] };

    runInAction(() => {
      this.ui.applicationSpecifications.setList(applicationSpecifications?.data || []);
      this.ui.applicationSpecificationsCategories.setList(applicationCategories.data || []);
    });
  }
  startPage = async (
    container: IContainer | null,
    mode: ApplicationDetailsMode,
    applicationId?: string,
    projectId?: string,
    type?: ApplicationDetailsType,
    projectFormDomain?: ProjectFormDomain,
  ) => {
    this.ui.isCompletePageStartLoading.setValue(false);
    this.projectFormDomain = projectFormDomain || null;
    this.container = container;
    this.ui.mode.setValue(mode);
    this.ui.type.setValue(type || ApplicationDetailsType.entity);
    applicationId = applicationId || this.ui.application.entity.id;
    projectId = projectId || this.ui.application.entity.projectId;

    await this.loadApplication(applicationId, projectId);

    this.ui.removeSelection();

    const application = this.ui.application.entity;
    const applicationData = this.ui.applicationData.entity;

    const startPageLoads: Promise<any>[] = [
      this.loadApplicationDataDependencies(applicationData),
      this.loadViewSpecifications(application),
      this.loadLearning(applicationId || ''),
    ];

    if (mode === ApplicationDetailsMode.edit) {
      startPageLoads.push(this.loadApplicationDependencies(application));
    }
    await Promise.all(startPageLoads);

    if (!this.ui.isFirstBootLoaded.value) {
      this.ui.isFirstBootLoaded.setValue(true);
    }

    if (mode === ApplicationDetailsMode.edit) {
      await this.startEditApplication();
      await this.getSpecificationsTextFieldsData();
    }
    this.ui.isCompletePageStartLoading.setValue(true);
  };

  setApplicationAcceptStatus(acceptStatus: any): void {
    if (JSON.stringify(this.ui.editApplication.entity) === JSON.stringify(defaultEditApplication)) {
      this.ui.editApplication.setEntity({ ...this.ui.application.entity });
    }
    this.ui.editApplication.entity.acceptStatus = acceptStatus;
  }

  private async loadApplication(applicationId?: string, projectId?: string) {
    const application = await this.getApplicationData(this.ui.type.value, applicationId, projectId);
    const activeUserId = this.layoutDomain.ui.activeUser.entity.id;
    const [project, applicationData, userRoles] = await Promise.all([
      application.projectId ? await this.rootService.project.entity.getById(application.projectId) : {},
      application.dataId
        ? await this.rootService.application.data.getById(application.dataId)
        : await this.rootService.application.data.preview(application),
      await this.rootService.projectRole.entity.search({ limit: 10000 }),
    ]);

    const userProjectRoleId = project.rolesMap?.data?.find((roleData) => roleData.userId === activeUserId)?.roleId;
    const userProjectRole = userRoles.data.find((userRole) => userRole.id === userProjectRoleId);
    const allProjectRoles = userRoles.data;

    const userProjectRoleActions = userProjectRole?.permissions.data
      .filter((item) => item.isGranted === true)
      .map((item) => item.action);

    // const isCanSeeRegulators =
    //   !!userProjectRoleActions?.includes(UserProjectRoleAction.showRegulators) ||
    //   this.layoutDomain.ui.activeUser.entity.allowedPermissions.includes(
    //     UserSystemRoleModelPermissionMap['project-not-in-team-permission-custom-requirement-access'],
    //   ) ||
    //   this.layoutDomain.ui.activeUser.entity.allowedPermissions.includes(
    //     UserSystemRoleModelPermissionMap['project-not-in-team-permission-show-regulators'],
    //   ) ||
    //   this.layoutDomain.ui.activeUser.entity.allowedPermissions.includes(
    //     UserSystemRoleModelPermissionMap['global-allow-any'],
    //   );

    const isCanUpdate =
      this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]) ||
      (this.layoutDomain.userHaveAnyAccess([
        UserSystemRoleModelPermissionMap['settings-application-template-update'],
      ]) &&
        this.ui.type.value === ApplicationDetailsType.template) ||
      this.layoutDomain.userHaveAnyAccess([
        UserSystemRoleModelPermissionMap['project-not-in-team-permission-update-application'],
      ]) ||
      !!userProjectRoleActions?.includes(UserProjectRoleAction.editApplication);

    const isCanDelete =
      this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]) ||
      this.layoutDomain.userHaveAnyAccess([
        UserSystemRoleModelPermissionMap['project-not-in-team-permission-delete-application'],
      ]) ||
      !!userProjectRoleActions?.includes(UserProjectRoleAction.editApplication) ||
      (this.layoutDomain.userHaveAnyAccess([
        UserSystemRoleModelPermissionMap['settings-application-template-delete'],
      ]) &&
        this.ui.type.value === ApplicationDetailsType.template);

    const isCanCreate =
      this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]) ||
      this.layoutDomain.userHaveAnyAccess([
        UserSystemRoleModelPermissionMap['project-not-in-team-permission-create-application'],
      ]) ||
      !!userProjectRoleActions?.includes(UserProjectRoleAction.creteApplication) ||
      (this.layoutDomain.userHaveAnyAccess([
        UserSystemRoleModelPermissionMap['settings-application-template-create'],
      ]) &&
        this.ui.type.value === ApplicationDetailsType.template);

    const isAdmin = this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]);
    // const isCanEditRequirementRemarks = this.layoutDomain.userHaveAnyAccess([], {
    //   id: this.ui.project.entity.id || '',
    //   permissions: ['writeRequirementRemarks'],
    // });

    runInAction(() => {
      // this.ui.editUsers.setList(applicationData.userList || []);
      this.ui.application.setEntity(application);
      this.ui.project.setEntity(project);
      this.ui.applicationData.setEntity(applicationData);
      this.ui.allProjectRoles.setValue(allProjectRoles);
      this.ui.isCanUpdate.setValue(isCanUpdate);
      this.ui.isCanDelete.setValue(isCanDelete);
      this.ui.isCanCreate.setValue(isCanCreate);
      // this.ui.isCanSeeRegulators.setValue(isCanSeeRegulators);
      this.ui.isAdmin.setValue(isAdmin);
      // this.ui.activeUserId.setValue(activeUserId);
      // this.ui.userProjectRoleActions.setValue(userProjectRoleActions || []);
      // this.ui.activeUserProjectRoleId.setValue(userProjectRoleId || '');
      // this.ui.isCanEditRequirementRemarks.setValue(isCanEditRequirementRemarks);
    });
  }

  async getApplicationData(type: ApplicationDetailsType, applicationId?: string, projectId?: string) {
    return applicationId
      ? await this.rootService.application.entity.getById(applicationId)
      : this.newApplicationModel(projectId);
  }

  private newApplicationModel(projectId?: string): IApplicationModel {
    return {
      name: '',
      projectId,
      specificationsIds: [],
    };
  }

  private async loadApplicationDataDependencies(applicationData: IApplicationDataModel) {
    if (!this.isLoadedApplicationDataDependencies) {
      const chatsCountersData = await (applicationData.id
        ? this.rootService.application.data.getApplicationDataChatCounters(applicationData.id || '')
        : null);

      this.isLoadedApplicationDataDependencies = true;
      this.ui.applicationNewMessagesState.setValue(chatsCountersData);
    }
  }

  private async loadApplicationDependencies(application: IApplicationModel) {
    const applicationSpecificationsIds = application.specificationsIds || null;
    const applicationPerformerId = application.performerTypeId || null;

    if (!this.isLoadedApplicationDependencies) {
      const [editSpecifications, editSpecificationsCategories, editPerformerTypes] = await Promise.all([
        this.rootService.specification.entity.customSearch({ limit: 100000 }, application.id),
        this.rootService.specification.category.search({ limit: 100000 }),
        this.rootService.performer.type.search({ limit: 100000 }),
      ]);

      runInAction(() => {
        this.ui.editSpecifications.setList(editSpecifications.data);
        this.ui.editSpecificationsCategories.setList(editSpecificationsCategories.data || []);
        this.ui.editPerformerTypes.setList(editPerformerTypes.data);
      });

      this.isLoadedApplicationDependencies = true;
    }

    const [applicationSpecifications, applicationPerformerType] = await Promise.all([
      applicationSpecificationsIds && applicationSpecificationsIds.length > 0
        ? this.rootService.specification.entity.search({
            limit: 100000,
            filter: { ids: { in: applicationSpecificationsIds } },
          })
        : null,
      applicationPerformerId ? this.rootService.performer.type.getById(applicationPerformerId) : null,
    ]);

    const applicationSpecificationsCategoriesIds: string[] =
      applicationSpecifications?.data.reduce((ids: string[], specification) => {
        //@ts-ignore
        if (!ids.includes(specification.categoryId)) {
          //@ts-ignore
          ids.push(specification.categoryId);
        }
        return ids;
      }, []) || [];

    const applicationSpecificationsCategories =
      applicationSpecificationsCategoriesIds?.length > 0
        ? await this.rootService.specification.category.search({
            limit: 100000,
            filter: { ids: { in: applicationSpecificationsCategoriesIds } },
          })
        : { data: [] };
    runInAction(() => {
      this.ui.applicationSpecificationsCategories.setList(applicationSpecificationsCategories.data);
      //@ts-ignore
      this.ui.applicationSpecifications.setList(applicationSpecifications?.data || []);
      this.ui.applicationPerformerType.setEntity(applicationPerformerType || {});
    });
  }

  startEditApplication = async () => {
    this.ui.editApplication.setEntity(this.ui.application.entity);
    // this.ui.previewApplicationData.setEntity(this.ui.applicationData.entity);
    this.ui.mode.setValue(ApplicationDetailsMode.edit);
    await this.updateSpecificationsDependence();
    await this.getSpecificationsTextFieldsData();
  };

  onClickEditApplication = () => {
    if (this.ui.application.entity.isUnitedWithProject) {
      this.router.goTo(
        `/project/edit/${this.ui.application.entity.projectId}/application/${this.ui.application.entity.id}`,
      );
    } else {
      this.startEditApplication();
    }
  };

  validateApplicationCategoryTextFields = async () => {
    const textFieldsInCurrentApplication = this.ui.specificationsTextFieldsValues.list.filter((item) => !!item.name);
    this.ui.specificationsTextFieldsValues.setList(textFieldsInCurrentApplication);
    const specificationsCategories = this.ui.editSpecificationsCategories.list;
    const validationErrors: string[] = [];
    if (textFieldsInCurrentApplication.length) {
      specificationsCategories.forEach((category) => {
        if (!category.isActive) {
          return;
        }
        const currentTextField = textFieldsInCurrentApplication.find((field) => field.categoryId === category.id);
        const isCategoryRequired = category.conditionsTextMap?.isRequired;
        const currentTextFieldName = currentTextField?.name.trim() || '';
        if (isCategoryRequired) {
          if (!currentTextFieldName) {
            validationErrors.push(category.id as string);
          }
        }
      });
    } else {
      specificationsCategories.forEach((category) => {
        if (category.conditionsTextMap?.isRequired && category.isActive) {
          validationErrors.push(category.id as string);
        }
      });
    }
    if (validationErrors.length) {
      this.ui.specificationsTextFieldsErrors.setValue(validationErrors);
    } else {
      this.ui.specificationsTextFieldsErrors.setValue([]);
    }
  };

  validateApplicationSpecifications = () => {
    let hasError = false;

    const specificationsCategoriesWithOneChoice = this.ui.editSpecificationsCategories.list.filter(
      (item) =>
        item.isActive &&
        item.categoryType === SpecificationCategoryType.specificationCategoryValue &&
        item.isSingleChoice,
    );

    const currentSpecificationsInApplication = this.ui.editSpecifications.list.filter((spec) => {
      return spec.id && !spec.applicationId && this.ui.editApplication.entity.specificationsIds?.includes(spec.id);
    });

    for (const category of specificationsCategoriesWithOneChoice) {
      const filteredValues = currentSpecificationsInApplication.filter((spec) => {
        return spec.categoryId === category.id;
      });

      if (filteredValues.length > 1) {
        hasError = true;
        break;
      }
    }
    return hasError;
  };

  saveValidation() {
    this.addApplicationValidationErrors([]);
    this.validateApplicationCategoryTextFields();
    const isHaveSpecificationValidationErrors = this.validateApplicationSpecifications();
    const isHaveTextFieldsValidationErrors = !!this.ui.specificationsTextFieldsErrors.value.length;
    const isHavePerformerTypeValidationError = !this.ui.editApplication.entity.performerTypeId;
    const isHaveNameValidationError =
      (this.projectFormDomain && !this.projectFormDomain.ui.model.entity.name) ||
      (!this.projectFormDomain && !this.ui.editApplication.entity.name);
    const errorsArr: any = [];

    if (isHaveSpecificationValidationErrors) {
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'В некоторых полях выбрано больше одного значения',
      });
    }
    if (isHaveTextFieldsValidationErrors) {
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'В системе имеются текстовые поля обязательные к заполнению.\nЗаполните их для дальнейших действий',
      });
    }
    if (isHavePerformerTypeValidationError) {
      this.ui.editApplication.entity.acceptStatus = ApplicationAcceptStatus.draft;
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'Ошибка валидации. Заполните "Тип исполнителей"',
      });
      const performerTypeError = {
        message: 'Значение не должно быть пустым',
        target: 'performerTypeId',
      };
      errorsArr.push(performerTypeError);
    }

    if (isHaveNameValidationError) {
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'Ошибка валидации. Заполните "Название"',
      });
      const nameError = {
        message: 'Длина меньше 2 символов',
        target: 'name',
      };
      errorsArr.push(nameError);
    }

    if (
      isHavePerformerTypeValidationError ||
      isHaveTextFieldsValidationErrors ||
      isHaveNameValidationError ||
      isHaveSpecificationValidationErrors
    ) {
      this.addApplicationValidationErrors([...errorsArr]);

      return true;
    }
    return false;
  }

  // TODO refactoring
  saveApplication = async () => {
    const isHaveNameValidationError = this.saveValidation();
    if (isHaveNameValidationError) return;
    let isFinalySave = true;
    if (this.projectFormDomain) {
      await this.projectFormDomain.save(false);
      //@ts-ignore
      isFinalySave = this.projectFormDomain?.isFinalySave?.value ?? true;
      if (!this.ui.editApplication.entity.performerTypeId) {
        this.ui.editApplication.setEntity(this.editApplicationCopy);
        this.ui.editApplication.entity.performerTypeId = this.performerTypeId;
      }
      if (this.projectFormDomain.ui.validationErrors.list.length === 0 && isFinalySave) {
        this.ui.editApplication.entity.projectId = this.projectFormDomain.ui.model.entity.id;
        this.ui.editApplication.entity.isUnitedWithProject = true;
        this.ui.editApplication.entity.name = this.projectFormDomain.ui.model.entity.name;
      }
    }

    if (
      !this.projectFormDomain ||
      (this.projectFormDomain && this.projectFormDomain.ui.validationErrors.list.length === 0 && isFinalySave)
    ) {
      this.ui.removeSelection();
      this.ui.isDisabledSaveButton.setValue(true);

      if (this.projectFormDomain) {
        this.ui.editApplication.entity.projectId = this.projectFormDomain.ui.model.entity.id;
        this.ui.editApplication.entity.isUnitedWithProject = true;
        this.ui.editApplication.entity.name = this.projectFormDomain.ui.model.entity.name;
      }
      await this.saveApplicationEntity();

      this.ui.isDisabledSaveButton.setValue(false);
    }
    return true;
  };

  saveApplicationEntity = async () => {
    this.removeApplicationValidationErrors();
    let applicationId = this.ui.application.entity.id;
    let projectId = this.ui.application.entity.projectId || this.ui.editApplication.entity.projectId;
    this.ui.editApplication.entity.projectId = projectId;
    if (applicationId) {
      const application = await this.rootService.application.entity
        .updateApplicationWithParams({
          newEntity: this.ui.editApplication.entity,
          textFieldsData: this.ui.specificationsTextFieldsValues.list,
        })
        .catch(this.errorsHandler);
      if (application) {
        this.ui.application.setEntity(application);
        this.ui.editApplication.setEntity(application);
      }
      // await Promise.all([this.saveApplicationStatuses(), this.saveApplicationAdditionalColumnData()]);
    } else {
      const application = (await this.rootService.application.entity
        .createApplicationWithParams({
          newEntity: this.ui.editApplication.entity,
          textFieldsData: this.ui.specificationsTextFieldsValues.list,
        })
        .catch(this.errorsHandler)) as IApplicationModel | undefined;
      applicationId = application?.id || undefined;
      if (application) {
        this.ui.editApplication.setEntity(application);
        this.ui.application.setEntity(application);
      }
      if (applicationId) {
        if (this.ui.applicationValidationErrors.list.length === 0) {
          const application = await this.rootService.application.entity.getById(applicationId);
          this.router.goTo(`/application/${applicationId}/info`);
        }
      }
    }

    if (this.ui.applicationValidationErrors.list.length === 0 && !this.projectFormDomain) {
      this.ui.isFirstBootLoaded.setValue(false);
      await this.startPage(this.container, ApplicationDetailsMode.view, applicationId, projectId);
    }

    if (this.ui.applicationValidationErrors.list.length === 0 && this.projectFormDomain) {
      this.ui.isFirstBootLoaded.setValue(false);
      this.router.goTo(`/application/${applicationId}/info`);
    }

    if (this.ui.applicationValidationErrors.list.length) {
      this.ui.editApplication.entity.acceptStatus = ApplicationAcceptStatus.draft;
    }
  };

  cancelEditApplication = () => {
    if (this.ui.application.entity.id) {
      if (this.projectFormDomain) {
        this.router.goTo(`/application/${this.ui.application.entity.id}/info`);
      } else {
        this.startPage(
          this.container,
          ApplicationDetailsMode.view,
          this.ui.application.entity.id,
          this.ui.project.entity.id,
        );
      }
    } else {
      if (this.ui.application.entity.projectId) {
        this.router.goTo(`/project/${this.ui.application.entity.projectId}/info`);
      } else {
        if (this.projectFormDomain) {
          this.router.goTo(`/`);
        } else {
          this.router.goTo(`/`);
        }
      }
    }
  };

  async deleteApplication() {
    const applicationId = this.ui.application.entity.id;
    if (applicationId) {
      await this.rootService.application.entity.deleteById(applicationId);
      if (this.ui.application.entity.isUnitedWithProject) {
        await this.rootService.project.entity.deleteById(this.ui.application.entity.projectId || '');
      }
      if (this.ui.application.entity.isUnitedWithProject) {
        this.router.goTo(`/`);
      } else {
        this.router.goTo(`/project/${this.ui.application.entity.projectId}/info`);
      }
    }
  }

  getUpdateFieldHandler(fieldName: string) {
    return (newValue: any) => {
      this.ui.editApplication.setEntity({
        ...this.ui.editApplication.entity,
        [fieldName]: newValue,
      });
    };
  }

  getUpdateSelectFieldHandler(fieldName: string) {
    return (newValue: any) => {
      this.ui.editApplication.setEntity({
        ...this.ui.editApplication.entity,
        [fieldName]: newValue?.value,
      });
      if ((fieldName = 'performerTypeId')) {
        this.performerTypeId = newValue?.value;
      }
    };
  }

  onChangeApplicationSpecifications(specificationIds: string[]) {
    this.ui.editApplication.entity.specificationsIds = [...specificationIds];
  }

  addApplicationValidationErrors(errors: IValidationErrorData[]) {
    this.ui.applicationValidationErrors.setList(errors);
  }

  getApplicationValidationErrorFor(fieldName: string, id?: string) {
    const error = this.ui.applicationValidationErrors.list.find(
      (validationError) => validationError.target === fieldName,
    );
    return error || null;
  }

  async removeApplicationValidationErrors() {
    this.ui.applicationValidationErrors.setList([]);
  }

  errorsHandler = async (error: IError, errorHandler?: (data: any) => void): Promise<void> => {
    if (error.webCode === '400') {
      errorHandler ? errorHandler(error.data) : this.addApplicationValidationErrors(error.data);
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'Ошибка валидации',
      });
    } else {
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'Неизвестная ошибка',
      });
    }
  };

  async downloadApplicationQuestionnaire({
    format = 'docx',
    audit,
    isNeedColor = false,
    reportName = '',
    versionNumber,
  }: {
    format?: string;
    audit?: ICoreSearchAudit;
    isNeedColor?: boolean;
    reportName?: string;
    versionNumber?: string;
  }) {
    const blobDocx = await this.rootService.project.entity.getReport(
      this.ui.application.entity.id,
      'docx',
      this.ui.application.entity.name,
      audit,
      isNeedColor,
    );

    this.saveFile(
      reportName
        ? `${reportName}.docx`
        : `${this.i18n.translate('phrases.automatizationSystem')} ${
            this.ui.application.entity.name
          } - ${this.i18n.translate('phrases.profileAS')}.docx`,
      blobDocx,
    );
  }

  saveFile(filename: string, blob: any) {
    saveFileInBrowser(filename, blob);
  }

  syncProjectAndApplicationDomains() {
    if (this.projectFormDomain) {
      this.projectFormDomain.applicationDomain.setEntity(this);
    }
  }

  async onEditCategoryTextField({ category, newValue }: { category: ISpecificationCategoryModel; newValue?: string }) {
    const valuesList = this.ui.specificationsTextFieldsValues.list;
    const currentFieldValue = valuesList.find((value) => value.categoryId === category.id);
    if (!newValue) {
      const newValueList = valuesList
        .map((value): ISpecificationEntityModel | null => {
          if (value.categoryId === category?.id && value?.id) {
            return { ...value, name: '' };
          }
          if (value.categoryId === category?.id && !value?.id) {
            return null;
          }
          return value;
        })
        .filter((item): item is ISpecificationEntityModel => item !== null);
      this.ui.specificationsTextFieldsValues.setList(newValueList);
    }
    if (currentFieldValue && newValue) {
      currentFieldValue.name = newValue;
      currentFieldValue.description = category.name;
      let newValueList = valuesList.filter((value) => value.categoryId !== currentFieldValue.categoryId);
      newValueList.push(currentFieldValue);
      this.ui.specificationsTextFieldsValues.setList(newValueList);
    }
    if (!currentFieldValue && newValue) {
      const newSpecification: ISpecificationEntityModel = {
        name: newValue,
        description: category.name,
        categoryId: category.id,
        isActive: true,
      };
      valuesList.push(newSpecification);
      this.ui.specificationsTextFieldsValues.setList(valuesList);
    }
  }

  async getSpecificationsTextFieldsData() {
    const applicationId = this.ui.editApplication.entity?.id || this.ui.application.entity.id;

    if (applicationId) {
      // редактирование анкеты
      const currentSpecifications = this.ui.editSpecifications.list.filter(
        (item) => item.applicationId === applicationId,
      );
      this.ui.specificationsTextFieldsValues.setList(currentSpecifications);
    }
    if (!applicationId) {
      // созадние анкеты без шаблона
      const defaultTextFields: ISpecificationEntityModel[] = [];
      this.ui.editSpecificationsCategories.list.forEach((specCategory) => {
        if (
          specCategory.categoryType === SpecificationCategoryType.specificationCategoryText &&
          specCategory.conditionsTextMap?.defaultValue &&
          specCategory.isActive
        ) {
          const newSpecification: ISpecificationEntityModel = {
            name: specCategory.conditionsTextMap?.defaultValue,
            description: specCategory.name,
            categoryId: specCategory.id,
            isActive: true,
          };
          defaultTextFields.push(newSpecification);
        }
      });
      this.ui.specificationsTextFieldsValues.setList(defaultTextFields);
    }
  }

  async updateSpecificationsDependence() {
    const editSpecifications = await this.rootService.specification.entity.customSearch(
      { limit: 100000 },
      this.ui.application.entity.id,
    );
    const editSpecificationsCategories = await this.rootService.specification.category.search({ limit: 100000 });
    runInAction(() => {
      this.ui.editSpecifications.setList((editSpecifications?.data as any) || []);
      this.ui.editSpecificationsCategories.setList(editSpecificationsCategories.data);
    });
  }

  async loadLearning(applicationId: string) {
    const resolvedApplicationId = applicationId || this.ui.application.entity.id || '';
    runInAction(() => {
      this.ui.learningIsLoading.setValue(true);
      this.ui.learningUnitList.setList([]);
      this.ui.learningGroupedUnits.setList([
        {
          groupName: this.i18n.translate('learningAdminPage.withoutContext'),
          unitsList: { required: [], notRequired: [] },
        },
      ]);
    });

    try {
      let specificationsIds: string[] = [];
      if (resolvedApplicationId !== '') {
        const loadedApplication =
          this.ui.application.entity.id === resolvedApplicationId ? this.ui.application.entity : null;
        specificationsIds =
          loadedApplication?.specificationsIds ||
          (await this.rootService.application.entity.getById(resolvedApplicationId)).specificationsIds ||
          [];
      }

      const applicationSpecifications: Set<string> = new Set(
        this.ui.mode.value === ApplicationDetailsMode.edit
          ? this.ui.editApplication.entity.specificationsIds || []
          : specificationsIds,
      );

      const isAdmin = this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]);

      runInAction(() => {
        this.ui.learningIsShowWarning.setValue(specificationsIds.length === 0);
        this.ui.learningApplicationSpecificationsSet.setValue(applicationSpecifications);
        this.ui.isAdmin.setValue(isAdmin);
      });

      const searchResult = await this.learningRootService.unit.search({
        limit: 1000000,
        fields: {
          include: [
            'settings.characteristicsIds',
            'settings.titleHeading',
            'isPublished',
            'settings.teamRolesIds',
            'score',
          ],
        },
      });

      const publishedUnits = searchResult.data.filter((unit) => unit.isPublished);

      const [{ data }, specificationSearchResult, { data: specificationCategoryData }] = await Promise.all([
        this.learningRootService.unitResult.search({
          filter: [{ fieldName: 'userId', type: 'equal', value: this.layoutDomain.ui.activeUser.entity.id }],
        }),
        this.rootService.specification.entity.search({
          limit: 100000,
          fields: ['name', 'categoryId'],
        }),
        this.rootService.specification.category.search({
          fields: ['name', 'id'],
          limit: 100000,
        }),
      ]);

      runInAction(() => {
        this.ui.learningUnitList.setList(searchResult.data);
        this.ui.learningPassingList.setList(publishedUnits);
        this.ui.learningPassingResultList.setList(data);
        this.ui.learningSpecificationCategory.setList(specificationCategoryData);
        this.ui.learningCharacteristicList.setList(specificationSearchResult.data);
      });
      this.getLearningAdditionalData();
      this.createLearningGroupedList(this.ui.learningUnitList.list);
      if (!this.ui.isAdmin.value) {
        const currentUserRoleMapping = this.ui.project.entity.rolesMap?.data?.find(
          (roleMap) => roleMap.userId === this.layoutDomain.ui.activeUser.entity.id,
        );
        const currentUserRoleId = currentUserRoleMapping?.roleId;

        if (currentUserRoleId) {
          this.filterLearningByRole(currentUserRoleId);
        }
      }
    } catch (error) {
      this.layoutDomain.notifications.showNotification({
        type: LayoutNotificationType.error,
        text: 'Не удалось загрузить обучение для системы',
      });
    } finally {
      this.ui.learningIsLoading.setValue(false);
    }
  }

  regroupLearning() {
    const specificationsIds =
      this.ui.mode.value === ApplicationDetailsMode.edit
        ? this.ui.editApplication.entity.specificationsIds || []
        : this.ui.application.entity.specificationsIds || [];

    this.ui.learningApplicationSpecificationsSet.setValue(new Set(specificationsIds));
    this.createLearningGroupedList(this.ui.learningUnitList.list);

    if (!this.ui.isAdmin.value) {
      const currentUserRoleMapping = this.ui.project.entity.rolesMap?.data?.find(
        (roleMap) => roleMap.userId === this.layoutDomain.ui.activeUser.entity.id,
      );
      const currentUserRoleId = currentUserRoleMapping?.roleId;
      if (currentUserRoleId) {
        this.filterLearningByRole(currentUserRoleId);
      }
    }
  }

  createLearningGroupedList = (unitList: IUnitModel[]) => {
    const charIdToCategoryMap = new Map();
    this.ui.learningCharacteristicList.list.forEach((char) => {
      charIdToCategoryMap.set(
        char.id,
        this.ui.learningSpecificationCategory.list.find((cat) => cat.id === char.categoryId),
      );
    });

    const groups: any = {
      [`${this.i18n.translate('learningAdminPage.withoutContext')}`]: {
        notRequired: [],
        required: [],
      },
    };

    unitList.forEach((unit) => {
      if (!unit.isPublished) {
        return;
      }
      let hasContext = false;

      unit.settings.characteristicsIds.forEach((charId) => {
        const category = charIdToCategoryMap.get(charId);
        if (category) {
          hasContext = true;
          if (!groups[category.name]) {
            groups[category.name] = {
              required: [],
              notRequired: [],
            };
          }

          let commonItem;
          if (!this.ui.learningRoleFilterIds.list.length) {
            commonItem = unit.settings.teamRolesIds.find((teamRoleId) =>
              this.ui.learningRolesWithUnits.list.some((item) => item.roleId === teamRoleId),
            );
          } else {
            commonItem = unit.settings.teamRolesIds.find((teamRoleId) =>
              this.ui.learningRoleFilterIds.list.includes(teamRoleId),
            );
          }

          const isMatchSpecification = unit.settings.characteristicsIds.some((charId) => {
            return this.ui.learningApplicationSpecificationsSet.value.has(charId);
          });

          if (commonItem && unit.settings.teamRolesIds.includes(commonItem) && isMatchSpecification) {
            groups[category.name].required.push(unit);
          } else {
            groups[category.name].notRequired.push(unit);
          }
        }
      });

      if (!hasContext && unit.settings.characteristicsIds.length === 0) {
        groups[`${this.i18n.translate('learningAdminPage.withoutContext')}`].notRequired.push(unit);
      }
    });

    const sortedGroupList = Object.keys(groups)
      .map((groupName) => ({ groupName, unitsList: groups[groupName] }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));

    const isAllNotRequired = sortedGroupList.every((item) => item.unitsList.required.length === 0);

    if (!this.ui.learningRolesWithUnits.list.length && this.ui.learningApplicationSpecificationsSet.value.size > 0) {
      this.ui.learningIsShowWarning.setValue(false);
    } else if (isAllNotRequired) {
      this.ui.learningIsShowWarning.setValue(true);
    } else {
      this.ui.learningIsShowWarning.setValue(false);
    }

    this.ui.learningGroupedUnits.setList(sortedGroupList);
  };

  redirect = async (id: string) => {
    this.router.goTo(ApplicationRoutes.LearningUnitPassing.replace(':passingId', id));
  };

  dropFilterByRole() {
    this.ui.learningRoleFilterIds.setList([]);
  }

  getLearningAdditionalData = () => {
    const rolesMapData = this.ui.project.entity.rolesMap?.data ?? [];
    const ids = rolesMapData.map((user) => user.userId);
    this.ui.learningTeamUserIds.setList(ids as string[]);
    const onlySelectedAppRoles = this.ui.allProjectRoles.value.filter((role) => {
      return rolesMapData.some((selectedRole) => selectedRole.roleId === role.id);
    });

    const rolesWithUnits = onlySelectedAppRoles
      .filter((role) => {
        return this.ui.learningUnitList.list.some(
          (unit) => unit.settings.teamRolesIds.includes(role.id as string) && unit.isPublished,
        );
      })
      .map((role) => ({ roleId: role.id as string, name: role.name }));
    this.ui.learningRolesWithUnits.setList(rolesWithUnits);
  };

  async filterLearningByRole(roleId: string) {
    if (this.ui.learningRoleFilterIds.list.includes(roleId)) {
      this.ui.learningRoleFilterIds.setList(this.ui.learningRoleFilterIds.list.filter((value) => value !== roleId));
    } else {
      this.ui.learningRoleFilterIds.setList([...this.ui.learningRoleFilterIds.list, roleId]);
    }

    await this.createLearningGroupedList(this.ui.learningUnitList.list);
  }

  redirectTo(url: string) {
    this.router.goTo(url);
  }

  redirectToKnowledge = () => {
    this.router.goTo(ApplicationRoutes.LearningRoot);
  };
  //todo Перенести в плагин ГПБ все что возможно

  // Сервисы которые не попадают в АСМ

  // async sendToManager() {
  //   try {
  //     await this.updateSpecificationsDependence();
  //     const applicationId = this.ui.editApplication.entity.id || this.ui.application.entity.id;
  //     if (applicationId) {
  //       const application = await this.rootService.application.entity.getById(applicationId);
  //       this.ui.editApplication.setEntity(application);
  //       this.ui.application.setEntity(application);
  //     }
  //     await this.getSpecificationsTextFieldsData();
  //     this.setApplicationAcceptStatus(ApplicationAcceptStatus.accepted);
  //     await this.getSpecificationsTextFieldsData();
  //     const result = await this.saveApplication();
  //     this.isDisabledSendToManager.setValue(false);
  //     return result;
  //   } catch (error) {
  //     this.isDisabledSendToManager.setValue(false);
  //     console.log('send to manager error', error);
  //   }
  // }

  // async endPsi() {
  //   await this.updateSpecificationsDependence();
  //   await this.getSpecificationsTextFieldsData();
  //   const applicationId = this.ui.editApplication.entity.id || this.ui.application.entity.id;
  //   if (applicationId) {
  //     const application = await this.rootService.application.entity.getById(applicationId);
  //     this.ui.editApplication.setEntity(application);
  //     this.ui.application.setEntity(application);
  //     await this.rootService.application.entity
  //       .updateApplicationWithParams({
  //         newEntity: application,
  //         textFieldsData: this.ui.specificationsTextFieldsValues.list,
  //       })
  //       .catch(this.errorsHandler);
  //     await this.loadApplication(applicationId);
  //   }
  //   await this.getSpecificationsTextFieldsData();
  // }

  // async loadApplicationDataPreview() {
  //   const application = this.ui.editApplication.entity;
  //   const applicationDataPreview = await this.rootService.application.data.preview(application);
  //   await this.loadApplicationDataDependencies(applicationDataPreview);
  //   this.ui.previewApplicationData.setEntity(applicationDataPreview);
  //   this.editApplicationCopy = this.ui?.editApplication?.entity;
  // }
}
